import { ResolvedSelection } from '@/feature/bet-share-sbk/utils/resolve-selections';
import { SbkBetSlipState } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { PromotionType } from '@/types/api.generated';
import { isOptionOpen, isProducerDown } from '@/utils/option-status';
import { roundToTwoDecimals } from '@/utils/round-two-decimals';
import { OddsUpdateMessageOption } from '@/utils/websocket/types';

import {
    Bet,
    BetSubmittedState,
    BetTypes,
    ComboBetType,
    ComboBetTypes,
    ConfirmedBetMapping,
    SbkBetSlip,
    SbkPromo,
    Selection,
} from '../types';

/** Betslip state slice needed for evaluating combo selection status */
export type ComboSelectionState = Pick<
    SbkBetSlipState,
    'selections' | 'options' | 'producerStatuses' | 'markets' | 'sgpOdds'
>;

/** Betslip state slice needed for computing combo/bet odds */
export type OddsComputationState = Pick<
    SbkBetSlipState,
    | 'eventOrder'
    | 'selections'
    | 'options'
    | 'producerStatuses'
    | 'markets'
    | 'oddsChanges'
    | 'sgpOddsChanges'
    | 'sgpOdds'
>;

/** Betslip state slice needed for checking betr bucks status */
export type BetrBucksCheckState = Pick<SbkBetSlipState, 'isBetrBucksEnabled'>;

/** Betslip state slice needed for full bet evaluation */
export type BetEvalState = OddsComputationState & BetrBucksCheckState;

export const calculatePayout = (bet: Bet, state: SbkBetSlip, isCashCurrency: boolean) => {
    const basePayout =
        bet.betType === BetTypes.Combo ? calculateComboPayout(bet, state) : calculateSinglesPayout(bet, state);
    return basePayout + getProfitBoostAddedProfit(bet, state, isCashCurrency);
};

export const calcProfitBoost = (odds: number, stake: number, promo: SbkPromo | null) => {
    if (!promo || promo.type !== PromotionType.ProfitBoost) {
        return 0;
    }
    // Stake above MAX_STAKE is allowed, but only the portion up to MAX_STAKE earns the boost
    const boostableStake = Math.min(stake, promo.metadata.MAX_STAKE ?? stake);
    const baseProfit = (odds - 1) * boostableStake;
    if (baseProfit <= 0) {
        return 0;
    }
    const boostedProfit = baseProfit * (promo.metadata.PROFIT_BONUS_PERCENT ?? 0);
    return roundToTwoDecimals(Math.min(boostedProfit, promo.metadata.MAX_PAYOUT_AMOUNT ?? boostedProfit));
};

export const getProfitBoostAddedProfit = (bet: Bet, state: SbkBetSlip, isCashCurrency: boolean) => {
    // Promotions only apply to cash currency, so ignore any selected promo in coins
    const promo = isCashCurrency ? state.selectedPromos[bet.id] : null;
    return calcProfitBoost(getBetOdds(bet, state), bet.stake ?? 0, promo);
};

const calculateSinglesPayout = (bet: Bet, state: SbkBetSlip) => {
    const odds = state.options[bet.id].odds;
    return adjustedPayout(odds, bet.stake ?? 0, isBetBetrBucks(bet.id, state));
};

const calculateComboPayout = (bet: Bet, state: SbkBetSlip) => {
    const odds = getComboOdds(state);
    return adjustedPayout(odds, bet.stake ?? 0, isBetBetrBucks(bet.id, state));
};

// helper functions to generate consistent bet ids
export const BetIds = {
    [BetTypes.Single]: (selection: Selection) => selection.id,
    [BetTypes.Combo]: BetTypes.Combo,
};

/**
 * Add eventId into eventOrder if that eventId does not currently exist within state.
 * @param eventId - the eventId to be added into eventOrder
 * @param eventOrder - bet slip state's eventOrder
 */
export const addEventOrder = (eventId: string, eventOrder: SbkBetSlip['eventOrder']) => {
    return eventOrder.includes(eventId) ? eventOrder : [...eventOrder, eventId];
};

export const adjustedPayout = (odds: number | false, stake: number, isBetrBucks: boolean) => {
    if (!odds || odds === 0) {
        return 0;
    }
    if (isBetrBucks) {
        return (odds - 1) * stake;
    }
    return odds * stake;
};

export const buildConfirmedSubmittedBets = (
    submittedBets: SbkBetSlip['submittedBets'],
    betIdMapping: ConfirmedBetMapping
): SbkBetSlip['submittedBets'] => {
    return Object.entries(submittedBets).reduce<SbkBetSlip['submittedBets']>((acc, [betId, bet]) => {
        const confirmed = betIdMapping[betId];
        acc[betId] = {
            ...bet,
            status: 'CONFIRMED',
            globalId: confirmed?.globalId ?? '',
            position: confirmed?.position ?? bet.position,
        };
        return acc;
    }, {});
};

export const getBetSummary = (state: SbkBetSlip, betCount: number) => {
    const submittedBets = Object.values(state.bets)
        .filter(b => isBetValid(b, state))
        .filter(b => b.stake);
    const wageredAmount = submittedBets.reduce((acc, bet) => acc + (bet.stake ?? 0), 0);
    return {
        totalCount: betCount,
        wageredAmount: wageredAmount,
    };
};

const createClosedSelection = (state: SbkBetSlipState, id: string) => {
    const selection = state.selections[id];
    const bet = state.bets[selection.id];
    return {
        option: state.options[selection.optionId],
        market: state.markets[selection.marketId],
        event: state.events[selection.eventId],
        bet,
    };
};

export const handleOddsUpdateMessages = (state: SbkBetSlipState, oddsUpdateMessages: OddsUpdateMessageOption[]) => {
    let options = { ...state.options };
    let oddsChanges = { ...state.oddsChanges };
    let closedSelections = [...state.closedSelections];
    let didStatusChange = false;
    oddsUpdateMessages.forEach(message => {
        const option = state.options[message.id];
        if (option) {
            if (!message.published) {
                if (!closedSelections.some(s => s.option.id === message.id)) {
                    closedSelections = [...closedSelections, createClosedSelection(state, message.id)];
                }
                options = { ...options, [message.id]: { ...option, status: 'SUSPENDED' } };
                didStatusChange = true;
            } else {
                // Remove from closed selections if market is republished
                if (closedSelections.some(s => s.option.id === message.id)) {
                    closedSelections = closedSelections.filter(s => s.option.id !== message.id);
                    didStatusChange = true;
                }

                // Handle status change
                if (message.status !== option.status) {
                    options = { ...options, [message.id]: { ...option, status: message.status } };
                    didStatusChange = true;
                }

                // Handle odds changes
                if (message.odds !== 0 && option.odds !== message.odds) {
                    options = {
                        ...options,
                        [message.id]: {
                            ...options[message.id],
                            odds: message.odds,
                        },
                    };
                    oddsChanges = { ...oddsChanges, [message.id]: option.odds };
                }
            }
        }
    });
    return {
        options,
        oddsChanges,
        closedSelections,
        didStatusChange,
    };
};

export const mapResolvedSelectionsToOddsUpdates = (selections: ResolvedSelection[]): OddsUpdateMessageOption[] =>
    selections.map(({ market, option }) => ({
        id: option.id,
        marketId: market.id,
        isMainLine: market.is_main_line,
        odds: option.odds,
        status: option.status,
        published: market.published,
        modifiers: [],
    }));

/**
 * Generates a unique id for a sgp bets based on the selection ids
 */
export const generateSgpOddsId = (selectionIds: string[], eventId: string) => {
    return [...selectionIds, eventId].sort().sort().join('-');
};

/**
 * Calculates the odds for a combo bet
 * groups the selectionIds by eventId
 * if there is only one selection for an event, use the selection's odds
 * if there are multiple selections for an event, use the sgpOdds
 * multiplies all the odds together to get combo odds
 */
export const getComboOdds = (state: OddsComputationState, isPrevious?: boolean) => {
    const odds = state.eventOrder.map(eventId => {
        const selectionIds = Object.values(state.selections)
            .filter(selection => isComboSelectionEnabled(selection, state) && selection.eventId === eventId)
            .map(selection => selection.id);

        if (selectionIds.length === 0) {
            return 1;
        }

        if (selectionIds.length === 1) {
            const currentOdds = state.options[selectionIds[0]].odds;
            if (isPrevious) {
                return state.oddsChanges[selectionIds[0]] || currentOdds;
            }
            return currentOdds;
        }
        const sgpOddsId = generateSgpOddsId(selectionIds, eventId);

        if (isPrevious) {
            return state.sgpOddsChanges[sgpOddsId] || state.oddsChanges[sgpOddsId] || state.sgpOdds[sgpOddsId] || 1;
        }
        return state.sgpOdds[sgpOddsId] || 1;
    });
    return roundToTwoDecimals(odds.reduce((acc, odd) => acc * odd, 1));
};

export const getBetOddsById = (state: OddsComputationState, betId: string) =>
    betId === BetIds.COMBO ? getComboOdds(state) : state.options[betId]?.odds ?? 0;

export const getBetOdds = (bet: Bet, state: OddsComputationState) => getBetOddsById(state, bet.id);

/**
 * Returns whether a bet has betr bucks enabled
 */
export const isBetBetrBucks = (betId: string, state: BetrBucksCheckState) => {
    return !!state.isBetrBucksEnabled[betId];
};

/**
 * Returns the adjusted odds for a bet if betr bucks is enabled
 */
export const getAdjustedOdds = (bet: Bet, state: OddsComputationState & BetrBucksCheckState) => {
    const odds = getBetOdds(bet, state);
    const isBetBucks = isBetBetrBucks(bet.id, state);
    return adjustOdds(odds, isBetBucks);
};

/**
 * Adjusts the odds based on whether betr bucks is enabled
 */
export const adjustOdds = (odds: number, isBetrBucks: boolean) => (isBetrBucks ? odds - 1 : odds);

/**
 * Returns whether a combo bet is valid based on the toggled selections
 */
export const isComboBetSelectionsValid = (state: ComboSelectionState) => {
    const openSelections = Object.values(state.selections).filter(
        selection => isComboSelectionEnabled(selection, state) && !isConflictingSelection(selection, state)
    );
    return openSelections.length > 1;
};

/**
 * Returns whether a bet is valid based on the toggled selections
 */
export const isBetSelectionsValid = (bet: Bet, state: ComboSelectionState) => {
    switch (bet.betType) {
        case BetTypes.Single:
            const option = state.options[bet.id];
            const market = state.markets[option?.marketId];
            return isOptionOpen(
                option?.status,
                isProducerDown(state.producerStatuses, market?.supplier),
                market?.published
            );
        case BetTypes.Combo:
            return isComboBetSelectionsValid(state);
    }
};

export const MAX_ODDS = 299; // Maximum allowable odds on any given bet

/**
 * Returns whether a bet has a max odds error
 * Odds cannot exceed 299
 */
export const hasMaxOddsError = (bet: Bet, state: OddsComputationState & BetrBucksCheckState) => {
    const odds = getAdjustedOdds(bet, state);
    return odds > MAX_ODDS;
};

/**
 * Returns whether a bet is valid
 * - Bet selections are valid
 * - Bet does not have a max odds error
 */
export const isBetValid = (bet: Bet, state: ComboSelectionState & BetEvalState) => {
    return isBetSelectionsValid(bet, state) && !hasMaxOddsError(bet, state);
};

/**
 * Determines combo bet type from selection counts per event
 * - 2+ events & 1 selection per event = PARLAY
 * - 1 event & 2+ selections = SGP
 * - 2+ events & at least 2+ selections for 1 event = SGP+
 */
export const getComboBetTypeFromGroupedSelections = (selectionCountsByEvent: number[]): ComboBetType => {
    const eventCount = selectionCountsByEvent.length;

    if (eventCount >= 2) {
        const hasOneSelectionPerEvent = selectionCountsByEvent.every(count => count === 1);
        if (hasOneSelectionPerEvent) {
            return ComboBetTypes.Parlay;
        }

        const hasAtLeast2SelectionsForOneEvent = selectionCountsByEvent.some(count => count > 1);
        if (hasAtLeast2SelectionsForOneEvent) {
            return ComboBetTypes.SGP_Plus;
        }
    }

    return ComboBetTypes.SGP;
};

/**
 * Returns the type of combo bet based on enabled selections
 */
export const getComboBetType = (state: ComboSelectionState): ComboBetType => {
    // group active/open selections by event
    const groupedSelections = Object.values(state.selections).reduce<Record<string, string[]>>((acc, selection) => {
        if (isComboSelectionEnabled(selection, state) && !isConflictingSelection(selection, state)) {
            acc[selection.eventId] = [...(acc[selection.eventId] ?? []), selection.id];
        }
        return acc;
    }, {});

    const selectionCounts = Object.values(groupedSelections).map(selections => selections.length);
    return getComboBetTypeFromGroupedSelections(selectionCounts);
};

/**
 * Returns whether a combo selection is enabled
 * A selection is enabled if:
 * - toggled on
 * - option status is OPENED
 */
export const isComboSelectionEnabled = (
    selection: Selection,
    state: Pick<SbkBetSlipState, 'options' | 'producerStatuses' | 'markets'>
) =>
    selection.isComboEnabled &&
    isOptionOpen(
        state.options[selection.optionId]?.status,
        isProducerDown(state.producerStatuses, state.markets[selection.marketId]?.supplier),
        state.markets[selection.marketId]?.published
    );

export const isConflictingSelection = (selection: Selection, state: ComboSelectionState) => {
    const activeSelectionIdsByEvent = Object.values(state.selections)
        .filter(s => s.eventId === selection.eventId && isComboSelectionEnabled(s, state))
        .map(s => s.id);
    const sgpId = generateSgpOddsId(activeSelectionIdsByEvent, selection.eventId);
    return state.sgpOdds[sgpId] === false;
};

export const groupSelectionIdsByEvent = (state: SbkBetSlip) => {
    return Object.values(state.selections).reduce<Record<string, Array<string>>>((acc, selection) => {
        if (!isComboSelectionEnabled(selection, state)) {
            return acc;
        }
        return {
            ...acc,
            [selection.eventId]: [...(acc[selection.eventId] || []), selection.id],
        };
    }, {});
};

export const hasAnySgpOddsChanged = (state: SbkBetSlip) => {
    const selectionIdsByEvent = groupSelectionIdsByEvent(state);
    return Object.entries(selectionIdsByEvent).some(([eventId, selectionIds]) => {
        const sgpId = generateSgpOddsId(selectionIds, eventId);
        return !!state.sgpOddsChanges[sgpId];
    });
};

export const hasAnyComboSelectionOddsChanged = (state: SbkBetSlip) => {
    return Object.values(state.selections)
        .filter(s => s.isComboEnabled)
        .some(s => !!state.oddsChanges[s.id]);
};

/**
 * Returns the selections that have been submitted but not been added back to the betslip (closed selections)
 */
export const getSubmittedNotAddedSelections = (
    selections: Record<string, Selection>,
    submittedState: BetSubmittedState
) => {
    const selectionIds = Object.keys(selections);
    const submittedStateSelectionId = Object.keys(submittedState.selections);
    const selectionNotAddedIds = submittedStateSelectionId.filter(id => !selectionIds.includes(id));
    return selectionNotAddedIds.map(id => {
        const selection = submittedState.selections[id];
        const { optionId, marketId, eventId } = selection;
        return {
            option: submittedState.options[optionId],
            market: submittedState.markets[marketId],
            event: submittedState.events[eventId],
        };
    });
};

/**
 * Returns whether odds have changed from their previous value
 */
export const hasOddsChanged = (currentOdds: number, previousOdds: number | undefined): boolean => {
    return previousOdds !== undefined && previousOdds !== currentOdds;
};
